import Immutable from 'seamless-immutable';
import { push } from 'connected-react-router';
import { retrieveErrors, retrieveSuccessMessage } from '@widergy/web-utils/lib/message';
import { formatDateToYYMMDDDD } from '@widergy/web-utils/lib/date';
import Polling from '@widergy/polling-handling';
import { AlertHandler } from '@widergy/energy-ui';

import * as CustomProceduresService from 'services/CustomProceduresService';
import * as AccountService from 'services/AccountService';
import {
  accountHolderChangeFields,
  WARRANTY_CHARGE_POSESSIONS,
  newServiceFields
} from 'app/screens/Dashboard/screens/AccountHolderChange/constants';
import { message } from 'utils/stringUtils';
import { ROUTES } from 'constants/routes';
import { appConfig } from 'config/customization';

import { stringArrayToObject } from './utils';
import { actions as paymentActions } from './paymentHandlers';
import { actionCreators as accountActions } from './accountHandlers';
import { actionCreators as alertActions } from './alertHandlers';

const SOLICITUD_PENDIENTE =
  'Ud. tiene la presente solicitud en curso sobre el suministro seleccionado. En la siguiente pantalla puede visualizar su estado y realizar las acciones que se encuentran disponibles sobre la misma.';

const getSelectedOptionId = (fields, key, option) => {
  const selectedField = fields.find(field => field.key === key);
  return (
    selectedField.field_options && selectedField.field_options.find(item => item.name === option && item.id)
  );
};

/* ------------- Custom procedures actions ------------- */
export const actions = stringArrayToObject(
  [
    'EVALUATE_ACCOUNT_HOLDER_CHANGE',
    'EVALUATE_ACCOUNT_HOLDER_CHANGE_FINISHED',
    'EVALUATE_TERMINATION_REQUEST',
    'EVALUATE_TERMINATION_REQUEST_FINISHED',
    'EVALUATE_CREATE_NEW_SERVICE',
    'EVALUATE_CREATE_NEW_SERVICE_FINISHED',
    'GET_ACCOUNT_DATA',
    'GET_ACCOUNT_DATA_SUCCESS',
    'GET_ACCOUNT_DATA_FAILURE',
    'CLEAN_ACCOUNT_DATA',
    'GET_ACCOUNT_TOTAL_DEBT',
    'GET_ACCOUNT_TOTAL_DEBT_SUCCESS',
    'GET_ACCOUNT_TOTAL_DEBT_FAILURE',
    'GET_ACCOUNT_DUE_DEBT',
    'GET_ACCOUNT_DUE_DEBT_SUCCESS',
    'GET_ACCOUNT_DUE_DEBT_FAILURE',
    'CREATE_HOLDER_CHANGE',
    'CREATE_HOLDER_CHANGE_SUCCESS',
    'CREATE_HOLDER_CHANGE_FAILURE',
    'GET_CUSTOM_PROCEDURE',
    'GET_CUSTOM_PROCEDURE_SUCCESS',
    'GET_CUSTOM_PROCEDURE_FAILURE',
    'GET_ACCOUNT_PROCEDURES',
    'GET_ACCOUNT_PROCEDURES_SUCCESS',
    'GET_ACCOUNT_PROCEDURES_FAILURE',
    'GET_TOTAL_ACCOUNTS',
    'GET_TOTAL_ACCOUNTS_SUCCESS',
    'GET_TOTAL_ACCOUNTS_FAILURE',
    'SET_NEXT_STEP',
    'SET_TEMP_ACCOUNT',
    'CREATE_NEW_SERVICE',
    'CREATE_NEW_SERVICE_SUCCESS',
    'CREATE_NEW_SERVICE_FAILURE',
    'CREATE_TERMINATE_REQUEST',
    'CREATE_TERMINATE_REQUEST_SUCCESS',
    'CREATE_TERMINATE_REQUEST_FAILURE',
    'CANCEL_CUSTOM_PROCEDURE',
    'CANCEL_CUSTOM_PROCEDURE_SUCCESS',
    'CANCEL_CUSTOM_PROCEDURE_FAILURE',
    'ENTITIES_LOGIN',
    'ENTITIES_LOGIN_SUCCESS',
    'ENTITIES_LOGIN_FAILURE'
  ],
  '@@CUSTOM-PROCEDURES'
);

const privateActionCreators = {
  entitiesLoginSuccess: payload => ({
    type: actions.ENTITIES_LOGIN_SUCCESS,
    payload,
    target: 'entitiesLogin'
  }),
  entitiesLoginFailure: payload => ({
    type: actions.ENTITIES_LOGIN_FAILURE,
    payload,
    target: 'entitiesLogin'
  })
};

export const terminationRequestSteps = stringArrayToObject([
  'AccountSelection',
  'TerminationRequestData',
  'TerminationRequestDetails'
]);

export const actionCreators = {
  evaluateAccountHolderChange(clientNumber, type) {
    return async dispatch => {
      dispatch({ type: actions.EVALUATE_ACCOUNT_HOLDER_CHANGE });
      const response = await Polling.handle(CustomProceduresService.evaluateChange(clientNumber, type));
      if (response.error) {
        dispatch(actionCreators.evaluateAccountHolderChangeFinished());
        if (retrieveErrors(response.error) === SOLICITUD_PENDIENTE) {
          dispatch(
            actionCreators.getAccountProcedures(clientNumber, 'CambioTitular', undefined, SOLICITUD_PENDIENTE)
          );
        } else {
          AlertHandler.error(
            retrieveErrors(response.error, message('DefaultMessages:evaluateAccountHolderChangeFailure'))
          );
        }
      } else {
        dispatch(actionCreators.evaluateAccountHolderChangeFinished(clientNumber));
        dispatch(actionCreators.getAccountData(clientNumber, false));
      }
    };
  },
  evaluateCreateNewService(clientNumber) {
    return async dispatch => {
      dispatch({ type: actions.EVALUATE_CREATE_NEW_SERVICE });
      const response = await Polling.handle(CustomProceduresService.evaluateNewService(clientNumber));
      if (response.error) {
        dispatch(actionCreators.evaluateCreateNewServiceFinished());
        if (retrieveErrors(response.error) === SOLICITUD_PENDIENTE) {
          dispatch(
            actionCreators.getAccountProcedures(
              clientNumber,
              'AltaSuministro',
              undefined,
              SOLICITUD_PENDIENTE
            )
          );
        } else {
          AlertHandler.error(
            retrieveErrors(response.error, message('DefaultMessages:evaluateAccountHolderChangeFailure'))
          );
        }
      } else {
        dispatch(actionCreators.evaluateCreateNewServiceFinished(clientNumber));
        dispatch(actionCreators.getAccountData(clientNumber, true));
      }
    };
  },
  evaluatePendingProcedures(procedureRequest) {
    return {
      type: actions.EVALUATE_PENDING_PROCEDURES,
      payload: procedureRequest
    };
  },
  evaluateAccountHolderChangeFinished(clientNumber) {
    return {
      type: actions.EVALUATE_ACCOUNT_HOLDER_CHANGE_FINISHED,
      payload: {
        clientNumber
      }
    };
  },
  evaluateCreateNewServiceFinished(clientNumber) {
    return {
      type: actions.EVALUATE_CREATE_NEW_SERVICE_FINISHED,
      payload: {
        clientNumber
      }
    };
  },
  getAccountData(clientNumber, newEP, nextStep) {
    return async dispatch => {
      dispatch({ type: actions.GET_ACCOUNT_DATA });
      const response = await Polling.handle(CustomProceduresService.retrieveAccountData(clientNumber));
      if (response.error) {
        dispatch(actionCreators.getAccountDataFailure());
        AlertHandler.error(retrieveErrors(response.data, message('DefaultMessages:getAccountDataFailure')));
      } else {
        dispatch(actionCreators.getAccountDataSuccess(response.data));
        dispatch(actionCreators.getAccountTotalDebt(clientNumber, newEP, nextStep));
      }
    };
  },
  getAccountDataSuccess(accountData) {
    return {
      type: actions.GET_ACCOUNT_DATA_SUCCESS,
      payload: {
        accountData
      }
    };
  },
  getAccountDataFailure() {
    return {
      type: actions.GET_ACCOUNT_DATA_FAILURE
    };
  },
  getAccountTotalDebt(clientNumber, newEP, nextStep) {
    return async dispatch => {
      dispatch({ type: actions.GET_ACCOUNT_TOTAL_DEBT });
      const response = await Polling.handle(CustomProceduresService.retrieveTotalDebt(clientNumber, newEP));
      if (response.error) {
        dispatch(actionCreators.getAccountTotalDebtFailure());
      } else {
        dispatch(actionCreators.getAccountTotalDebtSuccess(response.data));
        if (nextStep) {
          dispatch(actionCreators.setNextStep(nextStep));
        } else {
          dispatch(actionCreators.getAccountDueDebt(clientNumber, newEP));
        }
      }
    };
  },
  getAccountTotalDebtSuccess(totalDebt) {
    return {
      type: actions.GET_ACCOUNT_TOTAL_DEBT_SUCCESS,
      payload: {
        totalDebt
      }
    };
  },
  getAccountTotalDebtFailure() {
    return {
      type: actions.GET_ACCOUNT_TOTAL_DEBT_FAILURE
    };
  },
  getAccountDueDebt(clientNumber, newService) {
    return async dispatch => {
      dispatch({ type: actions.GET_ACCOUNT_DUE_DEBT });
      const response = await Polling.handle(
        CustomProceduresService.retrieveDueDebt(newService ? clientNumber : null)
      );
      if (response.error) {
        dispatch(actionCreators.getAccountDueDebtFailure());
      } else {
        dispatch(actionCreators.getAccountDueDebtSuccess(response.data));
      }
    };
  },
  getAccountDueDebtSuccess(dueDebt) {
    return {
      type: actions.GET_ACCOUNT_DUE_DEBT_SUCCESS,
      payload: {
        dueDebt
      }
    };
  },
  getAccountDueDebtFailure() {
    return {
      type: actions.GET_ACCOUNT_DUE_DEBT_FAILURE
    };
  },
  cleanAccountData: () => async (dispatch, getState) => {
    const { id } = getState().auth.currentUser;
    dispatch(accountActions.getAccountsAsync(id));
    dispatch({ type: actions.CLEAN_ACCOUNT_DATA });
  },
  createNewService(procedureData) {
    const supplyDestinationId = getSelectedOptionId(
      newServiceFields(),
      'supplyDestination',
      procedureData.supplyDestination
    );

    const createNewServiceData = {
      uso_suministro_id: supplyDestinationId && supplyDestinationId.key,
      client_number: procedureData.accountNumber,
      address: procedureData.address,
      actividad_id: ['Escritura', 'Boleto de compra-venta'].includes(procedureData.accreditPossession)
        ? null
        : '9999990'
    };
    return async dispatch => {
      dispatch({ type: actions.CREATE_NEW_SERVICE });
      const response = await Polling.handle(CustomProceduresService.createNewService(createNewServiceData));
      if (response.error) {
        dispatch(actionCreators.createNewServiceFailure());
        dispatch(
          alertActions.openModal({
            content: retrieveErrors(response.error, message('DefaultMessages:createHolderChangeFailure'))
          })
        );
      } else {
        dispatch(actionCreators.createNewServiceSuccess());
        dispatch(actionCreators.getCustomProcedure(response.data && response.data.Id));
      }
    };
  },
  createNewServiceSuccess() {
    return {
      type: actions.CREATE_NEW_SERVICE_SUCCESS
    };
  },
  createNewServiceFailure() {
    return {
      type: actions.CREATE_NEW_SERVICE_FAILURE
    };
  },
  createHolderChange(procedureData) {
    const supplyDestinationId = getSelectedOptionId(
      accountHolderChangeFields(),
      'supplyDestination',
      procedureData.supplyDestination
    );
    const accreditPossessionId = getSelectedOptionId(
      accountHolderChangeFields(),
      'accreditPossession',
      procedureData.accreditPossession
    );

    const accountHolderChangeData = {
      uso_suministro_id: supplyDestinationId && supplyDestinationId.key,
      tenencia: null, // TODO: This value is always null?
      tenencia_desde:
        formatDateToYYMMDDDD(procedureData.possessionDate || procedureData.contractInitialDate) || ' ',
      tenencia_hasta: procedureData.contractEndDate,
      acepto_reglamento: true,
      client_number: procedureData.accountNumber,
      direccion: procedureData.address,
      catastro: procedureData.cadastreP || 0,
      pre_partida: procedureData.cadastrePP || 0,
      tenencia_id: accreditPossessionId && accreditPossessionId.key,
      actividad_id: ['Escritura', 'Boleto de compra-venta'].includes(
        accreditPossessionId && accreditPossessionId.value
      )
        ? null
        : '9999990',
      deposito_en_garantia_proxima_factura:
        WARRANTY_CHARGE_POSESSIONS.indexOf(procedureData.accreditPossession) >= 0
          ? procedureData.warrantyCharge
          : 0,
      actividad: null // TODO: This value is always null?
    };

    return async dispatch => {
      dispatch({ type: actions.CREATE_HOLDER_CHANGE });
      const response = await Polling.handle(
        CustomProceduresService.createHolderChange(accountHolderChangeData)
      );
      if (response.error) {
        dispatch(actionCreators.createHolderChangeFailure());
        dispatch(
          alertActions.openModal({
            content: retrieveErrors(response.error, message('DefaultMessages:createHolderChangeFailure'))
          })
        );
      } else {
        dispatch(actionCreators.createHolderChangeSuccess());
        dispatch(actionCreators.getCustomProcedure(response.data && response.data.Id));
      }
    };
  },
  createHolderChangeSuccess() {
    return {
      type: actions.CREATE_HOLDER_CHANGE_SUCCESS
    };
  },
  createHolderChangeFailure() {
    return {
      type: actions.CREATE_HOLDER_CHANGE_FAILURE
    };
  },
  getCustomProcedure(procedureId, nextStep) {
    return async dispatch => {
      dispatch({ type: actions.GET_CUSTOM_PROCEDURE });
      const response = await Polling.handle(CustomProceduresService.getAccountHolderChange(procedureId));

      if (response.error) {
        dispatch(actionCreators.getCustomProcedureFailure());
      } else {
        if (nextStep) {
          dispatch(actionCreators.setNextStep(nextStep));
        }
        dispatch(actionCreators.getCustomProcedureSuccess(response.data));
      }
    };
  },
  getCustomProcedureSuccess(procedureData) {
    return {
      type: actions.GET_CUSTOM_PROCEDURE_SUCCESS,
      payload: {
        procedureData
      }
    };
  },
  getCustomProcedureFailure() {
    return {
      type: actions.GET_CUSTOM_PROCEDURE_FAILURE
    };
  },
  getAccountProcedures(clientNumber, type, nextStep, errorMsg) {
    return async dispatch => {
      dispatch({ type: actions.GET_CUSTOM_PROCEDURE });
      const response = await Polling.handle(CustomProceduresService.getAccountProcedures(clientNumber, type));
      if (response.error) {
        dispatch(actionCreators.getAccountProceduresFailure());
        AlertHandler.error(
          retrieveErrors(response.error, message('DefaultMessages:getAccountProceduresFailure'))
        );
      } else {
        if (errorMsg) AlertHandler.info(errorMsg);
        dispatch(actionCreators.getAccountProceduresSuccess());
        if (response.data.Estado !== 'Completed') {
          dispatch(actionCreators.getCustomProcedure(response.data.Id, nextStep));
        }
      }
    };
  },
  getAccountProceduresSuccess() {
    return {
      type: actions.GET_ACCOUNT_PROCEDURES_SUCCESS
    };
  },
  getAccountProceduresFailure() {
    return {
      type: actions.GET_CUSTOM_PROCEDURE_FAILURE
    };
  },
  getTotalAccounts() {
    return async dispatch => {
      dispatch({ type: actions.GET_TOTAL_ACCOUNTS });
      const response = await Polling.handle(
        AccountService.getAccountsAsync(appConfig.shouldUseAccOptimization),
        210000
      );
      if (response.error) {
        dispatch(push(ROUTES.ACCOUNT_STATUS));
        AlertHandler.info(message('TerminationRequest:accountIsEmpty'));
        dispatch(actionCreators.getTotalAccountsFailure());
      } else {
        dispatch(actionCreators.getTotalAccountsSuccess(response.data));
      }
    };
  },
  getTotalAccountsSuccess(totalAccounts) {
    return {
      type: actions.GET_TOTAL_ACCOUNTS_SUCCESS,
      payload: {
        totalAccounts
      }
    };
  },
  getTotalAccountsFailure() {
    return {
      type: actions.GET_TOTAL_ACCOUNTS_FAILURE
    };
  },
  setNextStep(nextStep) {
    return {
      type: actions.SET_NEXT_STEP,
      payload: { nextStep }
    };
  },
  setTempAccount(clientNumber) {
    return {
      type: actions.SET_TEMP_ACCOUNT,
      payload: { clientNumber }
    };
  },
  evaluateTerminationRequest(clientNumber, type) {
    return async dispatch => {
      dispatch({
        type: actions.EVALUATE_TERMINATION_REQUEST,
        payload: { clientNumber }
      });
      const response = await Polling.handle(CustomProceduresService.evaluateTermination(clientNumber, type));
      if (response.error) {
        dispatch(actionCreators.evaluateTerminationRequestFinished());
        if (retrieveErrors(response.error) === SOLICITUD_PENDIENTE) {
          AlertHandler.info(message('TerminationRequest:evaluateTerminationRequest'));
          dispatch(
            actionCreators.getAccountProcedures(
              clientNumber,
              'BajaSuministro',
              terminationRequestSteps.TerminationRequestDetails
            )
          );
        } else {
          AlertHandler.error(
            retrieveErrors(response.error, message('DefaultMessages:evaluateTerminationRequestFailure'))
          );
        }
      } else {
        dispatch(actionCreators.evaluateTerminationRequestFinished(clientNumber));
        dispatch(
          actionCreators.getAccountData(clientNumber, false, terminationRequestSteps.TerminationRequestData)
        );
      }
    };
  },
  evaluateTerminationRequestFinished(clientNumber) {
    return {
      type: actions.EVALUATE_TERMINATION_REQUEST_FINISHED,
      payload: {
        clientNumber
      }
    };
  },
  createTerminateRequest(procedureData) {
    return async dispatch => {
      dispatch({ type: actions.CREATE_TERMINATE_REQUEST });
      const response = await Polling.handle(
        CustomProceduresService.createTerminationRequest({
          client_number: procedureData.clientNumber,
          address: procedureData.address
        })
      );
      if (response.error) {
        dispatch(actionCreators.createTerminateRequestFailure());
        dispatch(
          alertActions.openModal({
            content: retrieveErrors(response.error, message('DefaultMessages:createHolderChangeFailure'))
          })
        );
      } else {
        dispatch(actionCreators.createTerminateRequestSuccess());
        dispatch(
          actionCreators.getCustomProcedure(
            response.data && response.data.Id,
            terminationRequestSteps.TerminationRequestDetails
          )
        );
      }
    };
  },
  createTerminateRequestSuccess() {
    return {
      type: actions.CREATE_TERMINATE_REQUEST_SUCCESS
    };
  },
  createTerminateRequestFailure() {
    return {
      type: actions.CREATE_TERMINATE_REQUEST_FAILURE
    };
  },
  cancelCustomProcedure(procedureId, procedureBody) {
    return async dispatch => {
      dispatch({ type: actions.CANCEL_CUSTOM_PROCEDURE });
      const response = await Polling.handle(
        CustomProceduresService.cancelCustomProcedure(procedureId, procedureBody)
      );
      if (response.error) {
        AlertHandler.error(
          retrieveErrors(response.error, message('DefaultMessages:cancelCustomProcedureFailure'))
        );
        dispatch(actionCreators.createTerminateRequestFailure());
      } else {
        dispatch(actionCreators.createTerminateRequestSuccess());
        AlertHandler.success(
          retrieveSuccessMessage(response.data, message('DefaultMessages:cancelCustomProcedureSuccess'))
        );
        dispatch(push(ROUTES.ACCOUNT_STATUS));
      }
    };
  },
  cancelCustomProcedureSuccess() {
    return {
      type: actions.CANCEL_CUSTOM_PROCEDURE_SUCCESS
    };
  },
  cancelCustomProcedureFailure() {
    return {
      type: actions.CANCEL_CUSTOM_PROCEDURE_FAILURE
    };
  },
  entitiesLogin: username => async dispatch => {
    dispatch({ type: actions.ENTITIES_LOGIN, target: 'entitiesLogin' });
    const response = await CustomProceduresService.entitiesLogin(username);
    if (response.ok) {
      CustomProceduresService.saveEntitySession(response.data && response.data.access_token);
      dispatch(privateActionCreators.entitiesLoginSuccess(username));
    } else {
      AlertHandler.error(retrieveErrors(response.data, message('DefaultMessages:entitiesLoginFailure')));
      dispatch(privateActionCreators.entitiesLoginFailure(response.data));
    }
  }
};

/* ------------- Custom procedures reducer ------------- */
const defaultState = {
  loading: false,
  totalAccountsLoading: false,
  accountData: null,
  accountTotalDebt: null,
  accountDueDebt: null,
  shouldShowAccountHolderChangeForm: false,
  shouldShowAccountHolderHistory: false,
  holderChangeTempClientNumber: null,
  newServiceTempClientNumber: null,
  tempClientNumber: null,
  customProcedureData: null,
  totalAccounts: null,
  shouldShowNewServiceForm: false,
  shouldShowNewServiceHistory: false,
  currentStep: terminationRequestSteps.AccountSelection,
  procedureRequestId: null
};

/* eslint-disable complexity */
export function reducer(state = Immutable(defaultState), action) {
  switch (action.type) {
    case actions.EVALUATE_ACCOUNT_HOLDER_CHANGE: {
      return Immutable.merge(state, {
        shouldShowAccountHolderChangeForm: false,
        shouldShowAccountHolderHistory: false,
        loading: true
      });
    }
    case actions.EVALUATE_ACCOUNT_HOLDER_CHANGE_FINISHED: {
      return Immutable.merge(state, {
        holderChangeTempClientNumber: action.payload.clientNumber,
        loading: false
      });
    }
    case actions.EVALUATE_CREATE_NEW_SERVICE: {
      return Immutable.merge(state, {
        shouldShowNewServiceForm: false,
        shouldShowNewServiceHistory: false,
        loading: true
      });
    }
    case actions.EVALUATE_CREATE_NEW_SERVICE_FINISHED: {
      return Immutable.merge(state, {
        newServiceTempClientNumber: action.payload.clientNumber,
        loading: false
      });
    }
    case actions.EVALUATE_TERMINATION_REQUEST: {
      return Immutable.merge(state, {
        tempClientNumber: action.payload.clientNumber,
        loading: true
      });
    }
    case actions.EVALUATE_TERMINATION_REQUEST_FINISHED: {
      return Immutable.merge(state, { tempClientNumber: action.payload.clientNumber, loading: false });
    }
    case actions.GET_ACCOUNT_DATA: {
      return Immutable.merge(state, { loading: true });
    }
    case actions.GET_ACCOUNT_DATA_SUCCESS: {
      return Immutable.merge(state, {
        accountData: action.payload.accountData,
        loading: false
      });
    }
    case actions.GET_ACCOUNT_DATA_FAILURE: {
      return Immutable.merge(state, {
        shouldShowAccountHolderChangeForm: false,
        shouldShowNewServiceForm: false,
        tempClientNumber: null,
        holderChangeTempClientNumber: null,
        newServiceTempClientNumber: null,
        loading: false
      });
    }
    case actions.GET_ACCOUNT_TOTAL_DEBT: {
      return Immutable.merge(state, { loading: true });
    }
    case actions.GET_ACCOUNT_TOTAL_DEBT_SUCCESS: {
      return Immutable.merge(state, { accountTotalDebt: action.payload.totalDebt, loading: false });
    }
    case actions.GET_ACCOUNT_TOTAL_DEBT_FAILURE: {
      return Immutable.merge(state, {
        shouldShowAccountHolderChangeForm: false,
        shouldShowNewServiceForm: false,
        tempClientNumber: null,
        holderChangeTempClientNumber: null,
        newServiceTempClientNumber: null,
        loading: false
      });
    }
    case actions.GET_ACCOUNT_DUE_DEBT: {
      return Immutable.merge(state, { loading: true });
    }
    case actions.GET_ACCOUNT_DUE_DEBT_SUCCESS: {
      return Immutable.merge(state, {
        accountDueDebt: action.payload.dueDebt,
        shouldShowAccountHolderChangeForm: true,
        shouldShowNewServiceForm: true,
        loading: false
      });
    }
    case actions.GET_ACCOUNT_DUE_DEBT_FAILURE: {
      return Immutable.merge(state, {
        shouldShowAccountHolderChangeForm: true,
        shouldShowNewServiceForm: true,
        loading: false
      });
    }
    case actions.CLEAN_ACCOUNT_DATA:
      return Immutable.merge(state, defaultState);
    case actions.CREATE_HOLDER_CHANGE: {
      return Immutable.merge(state, { loading: true });
    }
    case actions.CREATE_HOLDER_CHANGE_FAILURE: {
      return Immutable.merge(state, {
        holderChangeTempClientNumber: null,
        newServiceTempClientNumber: null,
        shouldShowAccountHolderChangeForm: false,
        shouldShowAccountHolderHistory: false,
        shouldShowNewServiceForm: false,
        shouldShowNewServiceHistory: false,
        loading: false
      });
    }
    case actions.CREATE_NEW_SERVICE: {
      return Immutable.merge(state, { loading: true });
    }
    case actions.CREATE_NEW_SERVICE_FAILURE: {
      return Immutable.merge(state, {
        newServiceTempClientNumber: null,
        shouldShowNewServiceForm: false,
        shouldShowNewServiceHistory: false,
        loading: false
      });
    }
    case actions.GET_CUSTOM_PROCEDURE: {
      return Immutable.merge(state, { loading: true });
    }
    case actions.GET_CUSTOM_PROCEDURE_SUCCESS: {
      return Immutable.merge(state, {
        shouldShowAccountHolderHistory: true,
        shouldShowNewServiceHistory: true,
        customProcedureData: action.payload.procedureData,
        loading: false
      });
    }
    case actions.GET_CUSTOM_PROCEDURE_FAILURE: {
      return Immutable.merge(state, {
        tempClientNumber: null,
        holderChangeTempClientNumber: null,
        newServiceTempClientNumber: null,
        loading: false
      });
    }
    case actions.GET_ACCOUNT_PROCEDURES: {
      return Immutable.merge(state, { loading: true });
    }
    case actions.GET_ACCOUNT_PROCEDURES_SUCCESS: {
      return Immutable.merge(state, { loading: false });
    }
    case actions.GET_ACCOUNT_PROCEDURES_FAILURE: {
      return Immutable.merge(state, { loading: false });
    }
    case actions.GET_TOTAL_ACCOUNTS: {
      return Immutable.merge(state, { totalAccountsLoading: true });
    }
    case actions.GET_TOTAL_ACCOUNTS_SUCCESS: {
      return Immutable.merge(state, {
        totalAccounts: action.payload.totalAccounts,
        totalAccountsLoading: false
      });
    }
    case actions.GET_TOTAL_ACCOUNTS_FAILURE: {
      return Immutable.merge(state, { totalAccountsLoading: false });
    }
    case actions.SET_NEXT_STEP: {
      return Immutable.merge(state, { currentStep: action.payload.nextStep });
    }
    case actions.SET_TEMP_ACCOUNT: {
      return Immutable.merge(state, { tempClientNumber: action.payload.clientNumber });
    }
    case actions.CREATE_TERMINATE_REQUEST: {
      return Immutable.merge(state, { loading: true });
    }
    case actions.CREATE_TERMINATE_REQUEST_FAILURE: {
      return Immutable.merge(state, { tempClientNumber: null, loading: false });
    }
    case actions.CANCEL_CUSTOM_PROCEDURE: {
      return Immutable.merge(state, { loading: true });
    }
    case actions.CANCEL_CUSTOM_PROCEDURE_SUCCESS: {
      return Immutable.merge(state, {
        shouldShowAccountHolderChangeForm: false,
        shouldShowAccountHolderHistory: false,
        shouldShowNewServiceForm: false,
        shouldShowNewServiceHistory: false,
        currentStep: terminationRequestSteps.AccountSelection,
        loading: false
      });
    }
    case actions.CANCEL_CUSTOM_PROCEDURE_FAILURE: {
      return Immutable.merge(state, {
        tempClientNumber: null,
        holderChangeTempClientNumber: null,
        newServiceTempClientNumber: null,
        loading: false
      });
    }
    case paymentActions.PAYMENT_REQUEST_SUCCESS: {
      return Immutable.merge(state, {
        tempClientNumber: null,
        newServiceTempClientNumber: null,
        holderChangeTempClientNumber: null,
        shouldShowAccountHolderChangeForm: false,
        shouldShowAccountHolderHistory: false,
        shouldShowNewServiceForm: false,
        shouldShowNewServiceHistory: false,
        paymentRequestSuccess: true
      });
    }
    case actions.ENTITIES_LOGIN: {
      return Immutable.merge(state, { entitiesLoginLoading: true, entitySession: null });
    }
    case actions.ENTITIES_LOGIN_SUCCESS: {
      return Immutable.merge(state, { entitiesLoginLoading: false, entitySession: action.payload });
    }
    case actions.ENTITIES_LOGIN_FAILURE: {
      return Immutable.merge(state, { entitiesLoginLoading: false, entitySession: null });
    }
    default: {
      return state;
    }
  }
}
